import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

// 모달 컴포넌트 (전역/헤더 관련)
import EditCustomerModal from './components/modals/EditCustomerModal';
import WeddingHallConfirmModal from './components/modals/WeddingHallConfirmModal';
import ContractModal from './components/modals/ContractModal';
import { type CustomerData } from './types';

// 탭 컴포넌트들 (이제 각자 로직을 가짐)
import OverviewTab from './components/tabs/OverviewTab';
import ProductsTab from './components/tabs/ProductsTab';
import PaymentsTab from './components/tabs/PaymentsTab';
import OrdersTab from './components/tabs/OrdersTab';
import ConsultationsTab from './components/tabs/ConsultationsTab';
import CustomerScheduleTab from './components/tabs/CustomerScheduleTab';

const toNumberOrUndefined = (value: string | number | undefined | null) => {
  if (value === undefined || value === null) return undefined;
  const cleaned = String(value).replace(/[^\d.-]/g, '');
  if (!cleaned) return undefined;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toDateOnlyOrUndefined = (value: string | undefined) => {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().split('T')[0];
};

const toDateTimeOrUndefined = (value: string | undefined) => {
  if (!value) return undefined;
  // "2026-04-01 12:00" or "2026-04-01T12:00" → ISO format "2026-04-01T12:00:00"
  const normalized = value.trim().replace(' ', 'T');
  if (normalized.length < 10) return undefined;
  // 이미 T 포함된 datetime-local 형식이면 그대로
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalized)) {
    return normalized.length === 16 ? `${normalized}:00` : normalized;
  }
  // 날짜만 있는 경우
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return `${normalized}T00:00:00`;
  }
  return undefined;
};

const normalizeCustomerStatus = (status?: string) => {
  const normalized = (status || '').trim();
  if (normalized === '계약완료') return '계약';
  if (normalized === '완료') return '본식완료';
  if (normalized === '취소됨') return '취소';
  return normalized;
};

const unwrapApiPayload = <T,>(result: any): T | null => {
  if (!result) return null;
  if (typeof result === 'object' && 'data' in result) return (result.data as T) ?? null;
  return result as T;
};

const formatCurrencyText = (value: string | number | undefined | null) => {
  if (value === undefined || value === null || value === '') return '-';
  const num = Number(String(value).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(num)) return '-';
  return `${num.toLocaleString()}원`;
};

export default function CustomerDetailView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id') || '1';
  
  // --- 상태 관리 ---
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'payments' | 'consultations' | 'orders' | 'schedule'>('overview');
  
  // 고객 기본 정보 State (헤더 표시용)
  const [customer, setCustomer] = useState<CustomerData>({
    id: customerId,
    // department: '',
    plannerName: '',

    applicationName: '',
    assignedStaffName: '', assignedStaffIdx: '',
    totalContractAmount: '',
    groomContact: '', brideContact: '',
    groomEmail: '', brideEmail: '',
    address: '', budget: '', referralSource: '', referralSourceDetail: '',
    contractType: '', contractDate: '', contractVendor: '',
    rehearsalDateTime: '', weddingDateTime: '',
    rehearsalLocation: '', weddingLocation: '',
    specialNote: '', status: '', category: ''
  });

  // 재무 요약 State (여러 탭에서 공유/업데이트 가능)
  const [financialSummary, setFinancialSummary] = useState({
    totalAmount: 0,
    totalPaid: 0,
    remainingAmount: 0
  });
  const [totalSupplyAmount, setTotalSupplyAmount] = useState(0);

  // 전역 모달 상태
  const [showWeddingHallConfirmModal, setShowWeddingHallConfirmModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  
  // 수정용 임시 데이터
  const [editCustomerData, setEditCustomerData] = useState(customer);
  const [weddingHallConfirmData, setWeddingHallConfirmData] = useState({
    sendDate: new Date().toISOString().split('T')[0],
    hallName: '', hallManager: '', hallPhone: '', hallFax: '',
    companyName: '체리쉬웨딩', companyManager: '', companyPhone: '02-1234-5678', companyFax: '02-1234-5679',
    initialGuarantee: 150, finalGuarantee: 150, rebateAmount: 5000000,
    accountInfo: '신한은행 110-123-456789 (주)체리쉬웨딩',
    notes: '웨딩홀 확인서 내용 확인 후 체크 및 담당자 서명 요청 드립니다.\n세금계산서 발행을 위한 역발주 시 사업자등록증(계좌 정보 포함) + 계산서용 이메일 함께 송부 부탁드립니다.'
  });

  // --- API: 고객 기본 정보 조회 (헤더용) ---
  const fetchCustomerBasicInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      // 기본 정보는 어떤 탭이든 필요하므로 OVERVIEW 탭 호출 등을 통해 가져옵니다.
      // 실제 API가 기본정보만 주는 엔드포인트가 있다면 거기를 쓰는게 좋습니다.
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=OVERVIEW`, {
         headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.status && result.data) {
        const { basicInfo, tabData } = result.data;
        // 총 계약금액: 고객 등록 시 입력한 값만 사용
        const resolvedTotalContractAmount = Number(basicInfo?.totalContractAmount ?? 0);

        const resolvedTotalPaidAmount =
          tabData?.totalPaidAmount ??
          tabData?.paidAmount ??
          tabData?.summary?.totalPaidAmount ??
          tabData?.stats?.totalPaidAmount ??
          0;

        setCustomer(prev => ({
          ...prev,
          id: basicInfo.customerIdx.toString(),
          applicationName: basicInfo.names,
          groomName: basicInfo.groomName,
          brideName: basicInfo.brideName,
          status: basicInfo.status,
          assignedStaffName: basicInfo.assignedStaffName,
          assignedStaffIdx: basicInfo.assignedStaffIdx,
          groomContact: basicInfo.groomContact,
          brideContact: basicInfo.brideContact,
          contractType: basicInfo.contractType || prev.contractType,
          contractDate: basicInfo.contractDate,
          contractVendor: basicInfo.weddingVenue,
          totalContractAmount: String(
            resolvedTotalContractAmount ??
            prev.totalContractAmount ??
            ''
          ),
          budget: String(basicInfo.budget ?? tabData?.budget ?? prev.budget ?? ''),
          referralSource: basicInfo.referralSource ?? tabData?.referralSource ?? prev.referralSource,
          referralSourceDetail: basicInfo.referralSourceDetail ?? tabData?.referralSourceDetail ?? prev.referralSourceDetail,
          specialNote: basicInfo.memo ?? tabData?.memo ?? prev.specialNote,
          weddingDateTime: basicInfo.weddingDate || '',
          weddingLocation: basicInfo.weddingVenue,
          // 리허설 정보는 탭 데이터에 있을 수도 있고 기본정보에 있을 수도 있음
          rehearsalDateTime: tabData?.rehearsalDate ? new Date(tabData.rehearsalDate).toLocaleDateString() : prev.rehearsalDateTime,
          rehearsalLocation: tabData?.rehearsalInfo || prev.rehearsalLocation,
        }));
        
        // 초기 로드시 금액 요약 정보도 업데이트
        if (tabData) {
            const tAmount = Number(resolvedTotalContractAmount ?? 0);
            const tPaid = Number(resolvedTotalPaidAmount ?? 0);
            const tRemaining = tAmount - tPaid;
            setFinancialSummary({ totalAmount: tAmount, totalPaid: tPaid, remainingAmount: tRemaining });
        }
      }
    } catch (error) {
      console.error('Failed to fetch basic info:', error);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerBasicInfo();
  }, [fetchCustomerBasicInfo]);

  // --- 핸들러: 고객 정보 수정/삭제 ---
  const handleEditCustomer = () => {
    setEditCustomerData(customer);
    setShowEditCustomerModal(true);
  };

  const handleSaveCustomer = async (data: CustomerData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const budget = toNumberOrUndefined(data.budget);
      const totalContractAmount = toNumberOrUndefined(data.totalContractAmount);
      const assignedStaffIdx = toNumberOrUndefined(data.assignedStaffIdx);
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          groomName: data.groomName,
          groomPhone: data.groomContact,
          groomEmail: data.groomEmail,
          brideName: data.brideName,
          bridePhone: data.brideContact,
          brideEmail: data.brideEmail,
          ...(budget !== undefined ? { budget } : {}),
          ...(totalContractAmount !== undefined ? { totalContractAmount } : {}),
          referralSource: data.referralSource,
          referralSourceDetail: data.referralSourceDetail,
          ...(assignedStaffIdx !== undefined ? { assignedStaffIdx } : {}),
          contractType: data.contractType,
          contractDt: data.contractDate,
          ...(toDateTimeOrUndefined(data.rehearsalDateTime)
            ? { rehearsalDt: toDateTimeOrUndefined(data.rehearsalDateTime) }
            : {}),
          rehearsalVenue: data.rehearsalLocation,
          ...(toDateTimeOrUndefined(data.weddingDateTime)
            ? { weddingDt: toDateTimeOrUndefined(data.weddingDateTime) }
            : {}),
          weddingVenue: data.weddingLocation,
          status: normalizeCustomerStatus(data.status),
          memo: data.specialNote,
        })
      });
      if (response.ok) {
        const result = await response.json().catch(() => null);
        const saved = unwrapApiPayload<any>(result);

        setCustomer(prev => ({
          ...prev,
          groomName: saved?.groomName ?? data.groomName ?? prev.groomName,
          brideName: saved?.brideName ?? data.brideName ?? prev.brideName,
          groomContact: saved?.groomPhone ?? data.groomContact ?? prev.groomContact,
          brideContact: saved?.bridePhone ?? data.brideContact ?? prev.brideContact,
          groomEmail: saved?.groomEmail ?? data.groomEmail ?? prev.groomEmail,
          brideEmail: saved?.brideEmail ?? data.brideEmail ?? prev.brideEmail,
          contractType: saved?.contractType ?? data.contractType ?? prev.contractType,
          contractDate: saved?.contractDt ?? data.contractDate ?? prev.contractDate,
          rehearsalLocation: saved?.rehearsalVenue ?? data.rehearsalLocation ?? prev.rehearsalLocation,
          weddingLocation: saved?.weddingVenue ?? data.weddingLocation ?? prev.weddingLocation,
          status: normalizeCustomerStatus(saved?.status ?? data.status ?? prev.status),
          referralSource: saved?.referralSource ?? data.referralSource ?? prev.referralSource,
          referralSourceDetail: saved?.referralSourceDetail ?? data.referralSourceDetail ?? prev.referralSourceDetail,
          specialNote: saved?.memo ?? data.specialNote ?? prev.specialNote,
          budget: String(saved?.budget ?? budget ?? prev.budget ?? ''),
          totalContractAmount: String(
            saved?.totalContractAmount ??
            totalContractAmount ??
            data.totalContractAmount ??
            prev.totalContractAmount ??
            ''
          ),
          assignedStaffIdx: String(saved?.assignedStaffIdx ?? assignedStaffIdx ?? prev.assignedStaffIdx ?? ''),
        }));

        alert('고객 정보가 수정되었습니다.');
        fetchCustomerBasicInfo(); 
        setShowEditCustomerModal(false);
      } else {
        const error = await response.json();
        alert(`수정 실패: ${error.message}`);
      }
    } catch (error) {
      console.error('Update Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!window.confirm('정말 이 고객 정보를 삭제하시겠습니까? 복구할 수 없습니다.')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('삭제되었습니다.');
        navigate('/dashboard/customer-detail');
      } else {
        const error = await response.json();
        alert(`삭제 실패: ${error.message}`);
      }
    } catch (error) {
      console.error('Delete Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const generateWeddingHallConfirmation = () => { console.log('PDF Generated'); };

  return (
    <div className="h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 p-6 overflow-y-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard/customer-detail')} className="text-gray-600 hover:text-gray-800 w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{customer.applicationName}</h1>
              <p className="text-gray-600 mt-1">고객 상세 정보</p>
            </div>
          </div>
          <div className="flex gap-3">
            {/* <button onClick={() => setShowWeddingHallConfirmModal(true)} className="bg-orange-600 text-white px-4 py-2.5 rounded-lg hover:bg-orange-700 transition-colors cursor-pointer text-sm flex items-center gap-2">
              <i className="ri-building-line"></i> 웨딩홀 확인서
            </button> */}
            <button onClick={() => setShowContractModal(true)} className="bg-purple-600 text-white px-4 py-2.5 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer text-sm flex items-center gap-2">
              <i className="ri-file-text-line"></i> 고객 계약서
            </button> 
            <button onClick={handleEditCustomer} className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm flex items-center gap-2">
              <i className="ri-edit-line"></i> 수정
            </button>
            <button onClick={handleDeleteCustomer} className="bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 transition-colors cursor-pointer text-sm flex items-center gap-2">
              <i className="ri-delete-bin-line"></i> 삭제
            </button>
          </div>
        </div>

        {/* 전역 모달들 */}
        <EditCustomerModal isOpen={showEditCustomerModal} onClose={() => setShowEditCustomerModal(false)} data={editCustomerData} onSave={handleSaveCustomer} />
        <WeddingHallConfirmModal isOpen={showWeddingHallConfirmModal} onClose={() => setShowWeddingHallConfirmModal(false)} data={weddingHallConfirmData} setData={setWeddingHallConfirmData} customer={customer} onGeneratePDF={generateWeddingHallConfirmation} />
        <ContractModal isOpen={showContractModal} onClose={() => setShowContractModal(false)} customer={customer} customerId={customerId} />

        {/* 고객 상세 정보 카드 (헤더 하단) */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
           <div className="grid grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-user-star-line text-blue-600"></i> 담당 정보</h3>
              <div className="space-y-3">
                <div><span className="text-sm text-gray-500">담당자</span><p className="font-medium text-gray-800">{customer.assignedStaffName}</p></div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-user-heart-line text-pink-600"></i> 고객 정보</h3>
              <div className="space-y-3">
                <div><span className="text-sm text-gray-500">신랑 연락처</span><p className="font-medium text-gray-800">{customer.groomContact}</p></div>
                <div><span className="text-sm text-gray-500">신부 연락처</span><p className="font-medium text-gray-800">{customer.brideContact}</p></div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-file-text-line text-green-600"></i> 계약 정보</h3>
              <div className="space-y-3">
                <div><span className="text-sm text-gray-500">계약일</span><p className="font-medium text-gray-800">{customer.contractDate}</p></div>
                <div><span className="text-sm text-gray-500">계약업체</span><p className="font-medium text-gray-800">{customer.contractVendor}</p></div>
                <div><span className="text-sm text-gray-500">총 계약금액</span><p className="font-medium text-gray-800">{formatCurrencyText(customer.totalContractAmount)}</p></div>
                <div><span className="text-sm text-gray-500">상태</span><span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-700">{customer.status}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* 주요 일정 섹션 */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
           <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2"><i className="ri-calendar-event-line text-purple-600"></i> 주요 일정</h3>
          <div className="grid grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><i className="ri-calendar-line text-blue-600"></i></div>
                <div><h4 className="font-semibold text-blue-800">리허설</h4><p className="text-sm text-blue-600">{customer.rehearsalDateTime ? new Date(customer.rehearsalDateTime).toLocaleDateString() : ''}</p></div>
              </div>
              <p className="text-sm text-blue-700">{customer.rehearsalLocation}</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center"><i className="ri-heart-line text-pink-600"></i></div>
                <div><h4 className="font-semibold text-pink-800">본식</h4><p className="text-sm text-pink-600">{customer.weddingDateTime ? new Date(customer.weddingDateTime).toLocaleDateString() : ''}</p></div>
              </div>
              <p className="text-sm text-pink-700">{customer.weddingLocation}</p>
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-8">
            {[
              { key: 'overview', label: '개요' },
              { key: 'products', label: '계약관리' },
              { key: 'payments', label: '입금관리' },
              { key: 'orders', label: '발주내역' },
              { key: 'consultations', label: '상담내역' },
              { key: 'schedule', label: '고객일정' }
            ].map((tab) => (
               <button
                 key={tab.key}
                 onClick={() => setActiveTab(tab.key as 'overview' | 'products' | 'payments' | 'orders' | 'consultations' | 'schedule')}
                 className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                   activeTab === tab.key ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'
                 }`}
               >
                 {tab.label}
               </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 영역 - 각 탭이 스스로 데이터를 로딩함 */}
        {activeTab === 'overview' && (
          <OverviewTab 
            customerId={customerId} 
            customer={customer}
            financialSummary={financialSummary}
          />
        )}

        {activeTab === 'products' && (
          <ProductsTab customerId={customerId} onSupplyAmountUpdate={setTotalSupplyAmount} />
        )}

        {activeTab === 'payments' && (
          <PaymentsTab
            customerId={customerId}
            totalContractAmount={Number(customer.totalContractAmount) || 0}
            totalSupplyAmount={totalSupplyAmount}
            onSummaryUpdate={setFinancialSummary}
          />
        )}

        {activeTab === 'orders' && (
          <OrdersTab customerId={customerId} customer={customer} />
        )}

        {activeTab === 'consultations' && (
          <ConsultationsTab customerId={customerId} />
        )}

        {activeTab === 'schedule' && (
          <CustomerScheduleTab customerId={customerId} customerName={customer.applicationName} />
        )}
      </div>
    </div>
  );
}
